/**
 * ErrorBoundary - 전역 에러 경계 컴포넌트
 * 
 * React 에러를 캐치하고 사용자 친화적인 폴백 UI를 표시합니다.
 * 개발 환경에서는 에러 스택을 표시하고, 프로덕션에서는 간단한 메시지만 표시합니다.
 */

import React, { Component, ReactNode } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // In production, you could send error to logging service
    if (import.meta.env.PROD) {
      // Example: Sentry, LogRocket, etc.
      // logErrorToService(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isDev = import.meta.env.DEV;
      const { error, errorInfo } = this.state;

      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="max-w-2xl w-full">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-destructive/10 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-destructive" />
                </div>
                <div>
                  <CardTitle>오류가 발생했습니다</CardTitle>
                  <CardDescription>
                    죄송합니다. 예상치 못한 문제가 발생했습니다.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* User-friendly error message */}
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>문제 해결 방법</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc list-inside space-y-1 mt-2">
                    <li>페이지를 새로고침해보세요</li>
                    <li>브라우저 캐시를 삭제해보세요</li>
                    <li>문제가 계속되면 홈페이지로 돌아가세요</li>
                  </ul>
                </AlertDescription>
              </Alert>

              {/* Development error details */}
              {isDev && error && (
                <div className="space-y-2">
                  <h3 className="font-semibold text-sm text-muted-foreground">개발자 정보:</h3>
                  <div className="bg-muted p-4 rounded-lg overflow-auto max-h-64">
                    <p className="font-mono text-xs text-destructive font-semibold mb-2">
                      {error.toString()}
                    </p>
                    {errorInfo && (
                      <pre className="font-mono text-xs text-muted-foreground whitespace-pre-wrap">
                        {errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-3">
                <Button 
                  onClick={this.handleReset} 
                  variant="default"
                  className="flex-1"
                  data-testid="button-error-retry"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  다시 시도
                </Button>
                <Button 
                  onClick={this.handleGoHome} 
                  variant="outline"
                  className="flex-1"
                  data-testid="button-error-home"
                >
                  <Home className="h-4 w-4 mr-2" />
                  홈으로
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
